import { useShortcuts } from '../../services/shortcuts/useShortcut';
import { seekStepSeconds } from './seek';

interface Params {
  /** Something is loaded; with nothing, the keys are left alone. */
  hasTrack: boolean;
  next(): void;
  prev(): void;
  /** Moves the playhead. False when the current surface cannot. */
  seekBy(seconds: number): boolean;
  toggleRepeat(): void;
}

/**
 * The transport on the keyboard, iTunes' bindings: ctrl+→ and ctrl+← for
 * the next and previous track, and shift+→ / shift+← to skip ten seconds
 * either way, which iTunes had on option+command. R cycles repeat like the
 * footer button.
 *
 * None of these while typing: ctrl+arrow jumps by word and shift+arrow
 * selects text in any box, and r is a letter. Seeking repeats when held;
 * changing track does not, or one long press would run through the queue.
 */
export default function usePlayerShortcuts({ hasTrack, next, prev, seekBy, toggleRepeat }: Params) {
  useShortcuts([
    {
      id: 'next-track',
      keys: { key: 'ArrowRight', mod: true },
      group: 'Playback',
      description: 'Next track',
      run: () => {
        if (!hasTrack) {
          return false;
        }

        next();
      },
    },
    {
      id: 'previous-track',
      keys: { key: 'ArrowLeft', mod: true },
      group: 'Playback',
      description: 'Previous track',
      run: () => {
        if (!hasTrack) {
          return false;
        }

        prev();
      },
    },
    {
      id: 'seek-forward',
      keys: { key: 'ArrowRight', shift: true },
      group: 'Playback',
      description: `Skip forward ${seekStepSeconds} seconds`,
      repeat: true,
      run: () => seekBy(seekStepSeconds),
    },
    {
      id: 'seek-back',
      keys: { key: 'ArrowLeft', shift: true },
      group: 'Playback',
      description: `Skip back ${seekStepSeconds} seconds`,
      repeat: true,
      run: () => seekBy(-seekStepSeconds),
    },
    {
      id: 'toggle-repeat',
      keys: { key: 'r' },
      group: 'Playback',
      description: 'Repeat: all, off, one',
      run: () => {
        toggleRepeat();
      },
    },
  ]);
}
